import { nextTick, onScopeDispose, ref, watch, type Ref } from 'vue';

type DialogEntry = { dialog: Ref<HTMLElement | null>; focus: () => void; previous: HTMLElement | null };
const dialogs: DialogEntry[] = [];
let originalOverflow = '';
let originalFocus: HTMLElement | null = null;

/** Nested dialogs share the scroll lock and return focus to the active layer. */
export function useDialogFocus(open: Ref<boolean>, close: () => void) {
  const dialog = ref<HTMLElement | null>(null);
  const closeButton = ref<HTMLButtonElement | null>(null);
  const entry: DialogEntry = { dialog, focus: () => closeButton.value?.focus(), previous: null };
  const release = () => {
    const index = dialogs.indexOf(entry);
    if (index < 0) return;
    const wasTop = index === dialogs.length - 1;
    dialogs.splice(index, 1);
    const top = dialogs[dialogs.length - 1];
    let target: HTMLElement | null = null;
    if (!top) {
      document.body.style.overflow = originalOverflow;
      target = originalFocus;
      originalFocus = null;
    } else if (wasTop) {
      if (entry.previous?.isConnected && top.dialog.value?.contains(entry.previous)) target = entry.previous;
    }
    if (!top || wasTop) void nextTick(() => {
      if (dialogs[dialogs.length - 1] !== top) return;
      if (target?.isConnected) target.focus();
      else top?.focus();
    });
    entry.previous = null;
  };
  watch(open, async visible => {
    if (typeof document === 'undefined') return;
    if (!visible) { release(); return; }
    if (dialogs.includes(entry)) return;
    entry.previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialogs.length) {
      originalOverflow = document.body.style.overflow;
      originalFocus = entry.previous;
    }
    dialogs.push(entry);
    document.body.style.overflow = 'hidden';
    await nextTick();
    if (open.value && dialogs[dialogs.length - 1] === entry) entry.focus();
  }, { immediate: true });
  const handleKeydown = (event: KeyboardEvent) => {
    if (dialogs[dialogs.length - 1] !== entry) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
    if (event.key !== 'Tab') return;
    const items = Array.from(dialog.value?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') || []).filter(item => item.getClientRects().length);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first) { event.preventDefault(); dialog.value?.focus(); }
    else if (event.shiftKey && (document.activeElement === first || !dialog.value?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !dialog.value?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  };
  onScopeDispose(() => { if (typeof document !== 'undefined') release(); });
  return { dialog, closeButton, handleKeydown };
}
